import { useRef } from "react";
import { useForm } from "react-hook-form";

import { BsPencilSquare } from "react-icons/bs";
import OverlayTooltip from "../../../OverlayTooltip";

import { Input } from "../../../Utility/FormElements";
import DialogModal from "../../../DialogModal";

import { cn } from "../../../../helpers/cn";

import {
  useUpdateCustomerReturnPermission,
  returnPermissionKeys,
  returnPermissionOptions,
} from "../returnPermission.util";

const formID = "edit-permission-form";

/**
 * @typedef {Object} Customer
 * @property {number} clientId
 * @property {number} customerId
 * @property {string} customerName
 * @property {string} customerAddress
 * @property {string} customerCity
 * @property {string} customerState
 * @property {string} customerZip
 * @property {string} reasonEntered
 * @property {string} enteredBy
 * @property {string} enteredDate
 * @property {string} permission
 */

/**
 * @typedef {Object} instance
 * @property {Object} row
 * @property {Customer} row.original
 * @property {Object} selectedFlatRows
 *
 * @param {instance} instance
 */

function EditPermissionActionCell(instance) {
  const {
    row: { original },
  } = instance;

  /**
   * @type {React.MutableRefObject<{show: function():void, hide: function():void} | null>} dialogModalRef
   */
  const dialogModalRef = useRef(null);

  const { isLoading, mutateUpdate } = useUpdateCustomerReturnPermission();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      customerId: original.customerId,
      permission: "",
    },
  });

  /**
   * @param {Object} submitData
   * @param {string} submitData.permission
   * @param {string} submitData.customerId
   */

  const onSubmit = (submitData) => {
    if (submitData.permission === original.permission) {
      // Close the modal if the permission is the same as the original permission
      reset();
      dialogModalRef.current?.hide();
      return;
    }

    const requestFormatData = {
      ClientId: original.clientId,
      CustomerId: original.customerId,
    };

    if (submitData.permission === returnPermissionKeys.allowedReturns) {
      mutateUpdate({
        ...requestFormatData,
        ByApproval: false,
        IsActive: false, // Disable the permission approval feature, allow returns freely
      });
    }

    if (
      submitData.permission === returnPermissionKeys.allowedReturnByApproval
    ) {
      mutateUpdate({
        ...requestFormatData,
        ByApproval: true,
        IsActive: true, // Enable the permission approval feature
      });
    }

    if (submitData.permission === returnPermissionKeys.notAllowedReturns) {
      mutateUpdate({
        ...requestFormatData,
        ByApproval: false,
        IsActive: true, // Not Allow returns
      });
    }

    reset();

    dialogModalRef.current?.hide();
  };

  const onCancel = () => {
    reset();
    dialogModalRef.current?.hide();
  };

  return (
    <div className="tw-flex tw-justify-center tw-items-center">
      <OverlayTooltip label={isLoading ? "Loading..." : "Edit"}>
        <button
          type="button"
          className={cn(
            "btn tw-border-0 tw-outline-none tw-grid tw-place-content-center hover:tw-bg-gray-200 tw-px-2",
            isLoading ? "tw-cursor-not-allowed tw-bg-gray-200" : ""
          )}
          onClick={() => {
            if (isLoading) return;

            dialogModalRef.current?.show();
          }}
        >
          <BsPencilSquare className="tw-h-5 tw-w-5" />
        </button>
      </OverlayTooltip>
      <DialogModal
        title="Edit Permission"
        ref={dialogModalRef}
        cancelButtonProps={{ onClick: onCancel }}
        saveButtonProps={{ form: formID, type: "submit", onClick: undefined }}
        onHide={onCancel}
      >
        <form id={formID} onSubmit={handleSubmit(onSubmit)}>
          <Input
            id="customer-id"
            label="Customer Id"
            disabled
            {...register("customerId")}
          />

          <div
            className={`tw-mt-1 ${errors.permission ? "tw-text-red-500" : ""}`}
          >
            Permission *
          </div>

          <select
            className={`form-control ${
              errors.permission
                ? "tw-border-red-500 focus:tw-border-red-500"
                : ""
            }`}
            {...register("permission", { required: true })}
          >
            <option value="">Select Permission</option>
            {returnPermissionOptions.map((option) => (
              <option key={option.value} value={option.label}>
                {option.label}
              </option>
            ))}
          </select>

          {errors.permission && (
            <small className="tw-text-red-500 tw-m-0">Required</small>
          )}
        </form>
      </DialogModal>
    </div>
  );
}

export default EditPermissionActionCell;
